<script setup lang="ts">
import { usePersonalAccountDataState } from '../../../../utilities/account/useAccountDataPersonal'
import AccountPanelPersonalMissingDataAlert from '../AccountPanelPersonalMissingDataAlert.vue'
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/vue/solid'
import { DocumentTextIcon, PrinterIcon, RefreshIcon } from '@heroicons/vue/outline'
import { ref, watch } from 'vue'
import SignSignatureDocumentButton from '../signatures/SignSignatureDocumentButton.vue'
import BaseButtonMuted from '../../../base/BaseButtonMuted.vue'
import { refreshNaturalProfileSignatures } from '../../../../utilities/api'
import FolderPlusIcon from '../../../base/icons/FolderPlusIcon.vue'
import { getPlatformSettings } from '../../../../utilities/usePlatformSettings'
import { isEsacNaturalComplianceProfileComplete } from '../../../wizard/ESAC/form/forms'
import { computedAsync } from '@vueuse/core'
import AccountPanelPersonalMissingComplianceProfileDataAlert from '../AccountPanelPersonalMissingComplianceProfileDataAlert.vue'
import { getAccountLink } from '../../../../utilities/useLinks'

const state = usePersonalAccountDataState()

const refreshingSignatures = ref(false)

async function refreshSignatures() {
    refreshingSignatures.value = true
    const response = await refreshNaturalProfileSignatures(
        state.naturalProfile.natural_profile_uid,
        encodeURI(getAccountLink())
    )
    state.naturalProfile.signatures = response.data
    refreshingSignatures.value = false
}

function openDocumentLink(url) {
    window.open(url, '_blank').focus()
}

const isComplianceProfileComplete = computedAsync(async () => {
    if (getPlatformSettings().compliance_profile === 'esac') {
        return await isEsacNaturalComplianceProfileComplete()
    }
    return true
}, false)

watch(isComplianceProfileComplete, () => {
    if (
        isComplianceProfileComplete.value === true &&
        getPlatformSettings().compliance_profile === 'esac' &&
        state.naturalProfile.signatures.length === 0
    ) {
        refreshSignatures()
    }
})
</script>

<template>
    <div>
        <AccountPanelPersonalMissingDataAlert
            v-if="!state.hasAllRequiredNaturalProfileAttributesForInvestment"
            inline
            :description-text="$t('signatures.complete_data_description_for_signatures')"
            class="my-9" />
        <AccountPanelPersonalMissingComplianceProfileDataAlert
            v-else-if="!isComplianceProfileComplete"
            inline
            :description-text="$t('signatures.complete_compliance_profile')"
            class="my-9" />
        <div v-else>
            <div class="grid grid-cols-1 gap-x-4 gap-y-8 px-4 py-5 sm:px-6">
                <div
                    v-if="state.naturalProfile.signatures.length > 0"
                    class="overflow-hidden border border-gray-200 bg-white shadow sm:col-span-1 sm:rounded-md">
                    <ul role="list" class="ml-0 list-none divide-y divide-gray-200">
                        <li v-for="signature in state.naturalProfile.signatures" :key="signature.link" class="mb-0">
                            <div>
                                <div class="flex items-center px-2 py-3 sm:px-4">
                                    <div class="min-w-0 flex-1 items-center md:flex">
                                        <div class="flex-shrink-0">
                                            <div
                                                class="relative flex items-center justify-center rounded-full bg-white p-1">
                                                <div class="relative">
                                                    <DocumentTextIcon
                                                        class="h-12 w-12 text-gray-300"></DocumentTextIcon>
                                                    <ExclamationCircleIcon
                                                        v-if="signature.signed_at === null"
                                                        class="absolute bottom-[-2px] right-[-2px] h-6 w-6 rounded-full bg-white text-red-400"></ExclamationCircleIcon>
                                                    <CheckCircleIcon
                                                        v-else
                                                        class="absolute bottom-[-2px] right-[-2px] h-6 w-6 rounded-full bg-white text-green-500"></CheckCircleIcon>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="min-w-0 flex-1 px-4">
                                            <div>
                                                <p class="truncate text-sm font-semibold text-gray-900">
                                                    {{ signature.document_type }}
                                                </p>
                                                <p class="mt-1 flex items-center text-sm text-gray-500">
                                                    Status:
                                                    {{
                                                        signature.signed_at
                                                            ? $t('signatures.signed')
                                                            : $t('signatures.not_signed')
                                                    }}
                                                </p>
                                            </div>
                                        </div>
                                        <div class="block pt-6 md:pt-0">
                                            <div class="flex flex-col space-y-2">
                                                <SignSignatureDocumentButton
                                                    class="px-8 font-bold"
                                                    :signature="signature">
                                                </SignSignatureDocumentButton>
                                                <BaseButtonMuted
                                                    :icon="PrinterIcon"
                                                    @click="openDocumentLink(signature.unsigned_document_link)"
                                                    >{{ $t('signatures.print') }}</BaseButtonMuted
                                                >
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </li>
                    </ul>
                </div>
                <div v-else class="pb-6 text-center">
                    <FolderPlusIcon class="mx-auto h-12 w-12 text-gray-400" />
                    <h3 class="mt-2 text-sm font-medium text-gray-900">{{ $t('signatures.no_signatures') }}</h3>
                    <p class="mt-1 text-sm text-gray-500">{{ $t('signatures.no_signatures_description') }}</p>
                </div>
                <div class="items-center justify-between space-y-2 md:flex md:space-y-0">
                    <p class="max-w-md pt-1 text-xs text-gray-400">
                        {{ $t('signatures.regenerate_signatures_notice') }}
                    </p>
                    <BaseButtonMuted
                        :loading="refreshingSignatures"
                        :icon="RefreshIcon"
                        size="lg"
                        @click="refreshSignatures()"
                        >{{ $t('signatures.regenerate_signatures') }}</BaseButtonMuted
                    >
                </div>
            </div>
        </div>
    </div>
</template>
